import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { useTranslation } from "react-i18next";
import AppInformation from './AppInformation';
import GeneralSettings from './GeneralSettings';
import Zones from './Zones';
import PartnerSettings from './PartnerSettings.js';
import AccessControl from './AccessControl';
import WebsiteStudio from './WebsiteStudio';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import PlaceIcon from '@mui/icons-material/Place';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';
import WebIcon from '@mui/icons-material/Public';
import { useSelector } from "react-redux";
import { hasPermission } from '../common/permissionUtils';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function Settings() {
  const [value, setValue] = React.useState(0);
  const { t } = useTranslation();
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);

  const canReadSettings = hasPermission(auth.profile, settings, 'settings', 'read');
  const canUpdateSettings = hasPermission(auth.profile, settings, 'settings', 'update');
  const canReadPartners = hasPermission(auth.profile, settings, 'partners', 'read');
  const canManageAccess = canUpdateSettings || hasPermission(auth.profile, settings, 'users', 'update');
  const canManageWebsite = canReadSettings;

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const tabStyle = (isActive) => ({
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 14,
    minHeight: 48,
    px: 2.5,
    mr: 1,
    borderRadius: '12px 12px 0 0',
    color: isActive ? '#111827' : '#6B7280',
    bgcolor: isActive ? '#FFFFFF' : 'transparent',
    border: isActive ? '1px solid #E5E7EB' : '1px solid transparent',
    borderBottom: isActive ? '1px solid #FFFFFF' : '1px solid transparent',
    '&:hover': {
      bgcolor: isActive ? '#FFFFFF' : '#F3F4F6',
    },
  });

  const tabs = [
    {
      key: 'app_info',
      label: t('app_info'),
      icon: <InfoIcon sx={{ fontSize: 18 }} />,
      show: canReadSettings,
      content: <AppInformation />,
    },
    {
      key: 'general_settings',
      label: t('general_settings'),
      icon: <SettingsIcon sx={{ fontSize: 18 }} />,
      show: canUpdateSettings,
      content: <GeneralSettings />,
    },
    {
      key: 'zones',
      label: t('zones'),
      icon: <PlaceIcon sx={{ fontSize: 18 }} />,
      show: canUpdateSettings,
      content: <Zones />,
    },
    {
      key: 'partners',
      label: t('partners') || 'Partners',
      icon: <BusinessIcon sx={{ fontSize: 18 }} />,
      show: canReadPartners,
      content: <PartnerSettings />,
    },
    {
      key: 'access_control',
      label: t('access_control') || 'Access control',
      icon: <SecurityIcon sx={{ fontSize: 18 }} />,
      show: canManageAccess,
      content: <AccessControl />,
    },
    {
      key: 'website_studio',
      label: t('website_studio') || 'Website Studio',
      icon: <WebIcon sx={{ fontSize: 18 }} />,
      show: canManageWebsite,
      content: <WebsiteStudio />,
    },
  ];

  const visibleTabs = tabs.filter((tab) => tab.show);

  React.useEffect(() => {
    if (value >= visibleTabs.length) {
      setValue(0);
    }
  }, [value, visibleTabs.length]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
      <Box
        sx={{
          borderBottom: '1px solid #E5E7EB',
          bgcolor: '#F9FAFB',
          px: 2,
          pt: 2,
          borderRadius: '16px 16px 0 0',
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="settings tabs"
          TabIndicatorProps={{ style: { display: 'none' } }}
        >
          {visibleTabs.map((tab, index) => (
            <Tab
              key={tab.key}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              sx={tabStyle(value === index)}
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
      </Box>
      {visibleTabs.map((tab, index) => (
        <TabPanel key={tab.key} value={value} index={index}>
          {tab.content}
        </TabPanel>
      ))}
    </Box>
  );
}
